import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {filter, take} from 'rxjs/operators';
import {Subscription, firstValueFrom} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {driver, DriveStep} from 'driver.js';
import {TokenDataProviderService} from '../services/token-data-provider.service';
import {TutorialRegistryService} from './tutorial-registry.service';
import {TutorialStateService} from './tutorial-state.service';
import {TutorialCampaignDefinition} from './models/tutorial-campaign-definition.interface';

@Injectable({
  providedIn: 'root'
})
export class TutorialOrchestratorService {
  private initialized = false;
  private activeCampaignKey: string | null = null;
  private routerSub?: Subscription;
  private activeDriver?: ReturnType<typeof driver>;
  private readonly stepTranslationKeys: Record<string, string> = {
    'tutorial-topbar-menu': 'tutorials.topbar.menu',
    'tutorial-account-shortcuts': 'tutorials.topbar.account_shortcuts',
    'tutorial-topbar-search': 'tutorials.topbar.search',
    'tutorial-topbar-notifications-mobile': 'tutorials.topbar.notifications',
    'tutorial-topbar-notifications-desktop': 'tutorials.topbar.notifications',
    'tutorial-footer-home': 'tutorials.footer.home',
    'tutorial-footer-feed': 'tutorials.footer.feed',
    'tutorial-footer-vehicles': 'tutorials.footer.vehicles',
    'tutorial-footer-events': 'tutorials.footer.events',
    'tutorial-footer-ranking': 'tutorials.footer.ranking',
    'tutorial-footer-add': 'tutorials.footer.add',
    'tutorial-footer-chat': 'tutorials.footer.chat',
    'tutorial-footer-marketplace': 'tutorials.footer.marketplace',
    'tutorial-footer-search': 'tutorials.footer.search',
    'tutorial-footer-profile': 'tutorials.footer.profile',
    'tutorial-breadcrumb-back': 'tutorials.breadcrumb.back',
    'tutorial-breadcrumb-scrolltop': 'tutorials.breadcrumb.scrolltop',
    'tutorial-breadcrumb-view-toggle': 'tutorials.breadcrumb.view_toggle',
    'tutorial-breadcrumb-profile': 'tutorials.breadcrumb.profile',
    'tutorial-breadcrumb-garage': 'tutorials.breadcrumb.garage',
    'tutorial-home-timeline': 'tutorials.home.timeline',
    'tutorial-garage-header': 'tutorials.garage.header',
    'tutorial-garage-profile-link': 'tutorials.garage.profile_link',
    'tutorial-garage-projects': 'tutorials.garage.projects',
    'tutorial-garage-create-project': 'tutorials.garage.create_project',
    'tutorial-garage-create-collaboration': 'tutorials.garage.create_collaboration',
    'tutorial-profile-social': 'tutorials.profile.social',
    'tutorial-profile-stats': 'tutorials.profile.stats',
    'tutorial-profile-tabs': 'tutorials.profile.tabs',
    'tutorial-profile-summary-garage': 'tutorials.profile.summary_garage',
    'tutorial-profile-summary-highlight': 'tutorials.profile.summary_highlight',
    'tutorial-profile-summary-events': 'tutorials.profile.summary_events',
    'tutorial-project-header': 'tutorials.project.header',
    'tutorial-project-actions': 'tutorials.project.actions',
    'tutorial-project-tabs': 'tutorials.project.tabs',
    'tutorial-project-collaborations': 'tutorials.project.collaborations',
    'tutorial-feed-list': 'tutorials.feed.list',
    'tutorial-events-filters': 'tutorials.events.filters',
    'tutorial-events-mode': 'tutorials.events.mode',
    'tutorial-events-list': 'tutorials.events.list',
    'tutorial-event-detail-actions': 'tutorials.event_detail.actions',
    'tutorial-event-detail-tabs': 'tutorials.event_detail.tabs',
    'tutorial-event-detail-content': 'tutorials.event_detail.content',
    'tutorial-marketplace-search': 'tutorials.marketplace.search',
    'tutorial-marketplace-advanced': 'tutorials.marketplace.advanced',
    'tutorial-marketplace-observed': 'tutorials.marketplace.observed',
    'tutorial-offers-list': 'tutorials.offers.list',
    'tutorial-chat-filters': 'tutorials.chat.filters',
    'tutorial-chat-threads': 'tutorials.chat.threads',
    'tutorial-chat-messages': 'tutorials.chat.messages',
    'tutorial-chat-input': 'tutorials.chat.input',
    'tutorial-settings-categories': 'tutorials.settings.categories',
    'tutorial-settings-notifications-content': 'tutorials.settings.notifications',
    'tutorial-settings-notifications': 'tutorials.settings.notifications',
    'tutorial-settings-account-content': 'tutorials.settings.account',
    'tutorial-settings-profile-content': 'tutorials.settings.profile',
    'tutorial-settings-preferences-content': 'tutorials.settings.preferences',
    'tutorial-settings-restart-content': 'tutorials.settings.restart',
    'tutorial-settings-account': 'tutorials.settings.account',
    'tutorial-settings-profile': 'tutorials.settings.profile',
    'tutorial-settings-preferences': 'tutorials.settings.preferences',
    'tutorial-settings-restart': 'tutorials.settings.restart',
  };
  private readonly preventScrollHandler = (event: Event) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.driver-popover')) {
      return;
    }

    event.preventDefault();
  };

  constructor(
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly tokenDPS: TokenDataProviderService,
    private readonly tutorialRegistry: TutorialRegistryService,
    private readonly tutorialState: TutorialStateService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {
  }

  public init(): void {
    if (this.initialized || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.initialized = true;
    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        void this.startCurrentRouteTutorial();
      });

    void this.startCurrentRouteTutorial();
  }

  public async startCurrentRouteTutorial(force = false): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId) || this.activeCampaignKey) {
      return false;
    }

    if (!this.tokenDPS.isAuthenticatedFully()) {
      return false;
    }

    const campaign = this.tutorialRegistry.getMatchingCampaign(this.router.url);
    if (!campaign) {
      return false;
    }

    return this.startCampaign(campaign.key, force);
  }

  public async startMatchingCampaignForCurrentRoute(force = false): Promise<boolean> {
    return this.startCurrentRouteTutorial(force);
  }

  public async startCampaign(campaignKey: string, force = false): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId) || this.activeCampaignKey) {
      return false;
    }

    const campaign = this.tutorialRegistry.getCampaignByKey(campaignKey);
    if (!campaign) {
      return false;
    }

    const currentUser = await firstValueFrom(this.tokenDPS.currentUser.pipe(take(1)));
    const mergedState = await firstValueFrom(this.tutorialState.syncStateWithCurrentUser().pipe(take(1)));

    if (!currentUser?.id || (!force && this.tutorialState.isCampaignCompleted(mergedState, campaign))) {
      return false;
    }

    await campaign.beforeStart?.();

    const availableSteps = await this.waitForSteps(campaign);
    if (!availableSteps.length) {
      return false;
    }

    this.activeCampaignKey = campaign.key;
    this.lockScroll();

    let campaignStatus: 'completed' | 'skipped' | null = null;
    let finalized = false;
    let driverInstance!: ReturnType<typeof driver>;

    driverInstance = driver({
      animate: true,
      smoothScroll: true,
      allowClose: true,
      overlayOpacity: 0.55,
      stagePadding: 8,
      popoverOffset: 12,
      showProgress: true,
      showButtons: ['previous', 'next', 'close'],
      prevBtnText: this.translate.instant('tutorials.common.prev'),
      nextBtnText: this.translate.instant('tutorials.common.next'),
      doneBtnText: this.translate.instant('tutorials.common.done'),
      popoverClass: 'wtg-tutorial-popover',
      steps: this.buildDriverSteps(availableSteps),
      onHighlighted: () => {
        window.setTimeout(() => this.activeDriver?.refresh(), 0);
      },
      onPrevClick: () => {
        driverInstance.movePrevious();
      },
      onNextClick: () => {
        if (driverInstance.isLastStep()) {
          campaignStatus = 'completed';
          driverInstance.destroy();
          return;
        }

        driverInstance.moveNext();
      },
      onCloseClick: () => {
        campaignStatus = 'skipped';
        driverInstance.destroy();
      },
      onDestroyed: () => {
        if (finalized) {
          return;
        }

        finalized = true;
        this.activeDriver = undefined;
        if (campaignStatus) {
          void this.finishCampaign(campaign, campaignStatus);
          return;
        }

        this.activeCampaignKey = null;
        this.unlockScroll();
      },
    });

    this.activeDriver = driverInstance;
    driverInstance.drive();

    return true;
  }

  public resetAllProgress() {
    return this.tutorialState.resetCampaignProgress(this.tutorialRegistry.getCampaignKeys());
  }

  private async finishCampaign(
    campaign: TutorialCampaignDefinition,
    status: 'completed' | 'skipped',
  ): Promise<void> {
    await firstValueFrom(
      this.tutorialState.recordCampaignProgress(campaign.key, campaign.version, status).pipe(take(1))
    );
    this.activeCampaignKey = null;
    this.unlockScroll();
  }

  private async waitForSteps(campaign: TutorialCampaignDefinition): Promise<string[]> {
    const waitTimeoutMs = campaign.waitTimeoutMs || 3000;
    const start = Date.now();
    let lastCount = -1;
    let stablePolls = 0;

    while (Date.now() - start < waitTimeoutMs) {
      const availableSteps = this.getAvailableSteps(campaign.steps);

      if (availableSteps.length === campaign.steps.length) {
        return availableSteps;
      }

      if (availableSteps.length > 0) {
        if (availableSteps.length === lastCount) {
          stablePolls++;
        } else {
          stablePolls = 0;
        }

        if (stablePolls >= 2) {
          return availableSteps;
        }
      }

      lastCount = availableSteps.length;
      await this.sleep(250);
    }

    return this.getAvailableSteps(campaign.steps);
  }

  private getAvailableSteps(steps: string[]): string[] {
    return steps.filter((step) => this.hasVisibleTarget(step));
  }

  private buildDriverSteps(steps: string[]): DriveStep[] {
    return steps.map((step) => ({
      element: () => this.findVisibleTarget(step) || undefined,
      popover: {
        title: this.translate.instant(`${this.getTranslationKey(step)}.title`),
        description: this.translate.instant(`${this.getTranslationKey(step)}.text`),
        side: this.getPopoverSide(step),
        align: 'center',
      },
    }));
  }

  private sleep(delayMs: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  private hasVisibleTarget(step: string): boolean {
    const elements = Array.from(this.document.querySelectorAll(`[data-tutorial="${step}"]`));

    return elements.some((element) => this.isVisible(element as HTMLElement));
  }

  private findVisibleTarget(step: string): HTMLElement | null {
    const elements = Array.from(this.document.querySelectorAll(`[data-tutorial="${step}"]`));

    return (elements.find((element) => this.isVisible(element as HTMLElement)) as HTMLElement) || null;
  }

  private isVisible(element: HTMLElement): boolean {
    if (!element) {
      return false;
    }

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }

    const rect = element.getBoundingClientRect();

    return rect.width > 0 && rect.height > 0;
  }

  private getTranslationKey(step: string): string {
    return this.stepTranslationKeys[step] || 'tutorials.home.timeline';
  }

  private getPopoverSide(step: string): 'top' | 'bottom' | 'left' | 'right' {
    if (step.startsWith('tutorial-footer')) {
      return 'top';
    }

    if (step.startsWith('tutorial-topbar')) {
      return 'bottom';
    }

    if (step.includes('chat-input')) {
      return 'top';
    }

    return 'bottom';
  }

  private lockScroll(): void {
    window.addEventListener('wheel', this.preventScrollHandler, {passive: false});
    window.addEventListener('touchmove', this.preventScrollHandler, {passive: false});
  }

  private unlockScroll(): void {
    window.removeEventListener('wheel', this.preventScrollHandler);
    window.removeEventListener('touchmove', this.preventScrollHandler);
  }
}
